import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { X, Check, Utensils, ChevronDown } from "lucide-react";
import { api, describeRequestError } from "@/lib/api";
import { usePhases } from "@/lib/phases";
import { phaseSplit, round2 } from "@/lib/util";
import { toast } from "sonner";

/** One day, entered like a spreadsheet: every phase is its own row, every row
 *  its own box. No picking a phase first and typing one total to divide --
 *  each number is typed where it belongs.
 *
 *  A row is a thin line until it holds something. Tap it open to type an
 *  hours figure; tap it again while it's still empty and it closes back to
 *  thin. The moment it holds a number it stops collapsing, so a glance down
 *  the list shows exactly which phases got logged today and which didn't --
 *  per Kim's spec (session design review, Sep 2026).
 *
 *  The day total lives in its own card pinned under the header, live on
 *  every keystroke -- it is exactly the sum of what's typed on phases. The
 *  meal toggle sits beside it as a status flag, not more arithmetic: it's
 *  recorded on save, but never added to or taken off that number. Tapping a
 *  box never opens the system keyboard -- a thumb-height number pad opens at
 *  the bottom instead, same one for every field on the screen.
 */

const MEAL_MINUTES_ON = 30;

/* ------------------------------------------------------------ number pad -- */

const PAD_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "back"];
const PAD_PRESETS = [2, 4, 6, 8];

function NumPad({ label, value, onKey, onPreset, onDone, onClear }) {
    return (
        <div className="px-2.5 pt-2 pb-3" data-testid="numpad">
            <div className="flex items-center justify-between px-1 pb-2">
                <span className="text-[11.5px] font-bold text-brand-charcoal/60">{label}</span>
                <div className="flex items-center gap-2">
                    {value && (
                        <button type="button" onClick={onClear} data-testid="numpad-clear"
                                className="text-[11px] font-bold text-brand-danger underline underline-offset-2">
                            Clear
                        </button>
                    )}
                    <span className="font-serif font-bold text-xl text-brand-navy tabular-nums" data-testid="numpad-value">
                        {value || "0"}
                    </span>
                </div>
            </div>
            <div className="flex gap-1.5 pb-2">
                {PAD_PRESETS.map((p) => (
                    <button key={p} type="button" onClick={() => onPreset(p)}
                            data-testid={`numpad-preset-${p}`}
                            className="flex-1 h-8 rounded-lg border-[1.5px] border-brand-charcoal/20 bg-white font-bold text-[12.5px] text-brand-charcoal">
                        {p}h
                    </button>
                ))}
            </div>
            <div className="grid grid-cols-3 gap-1.5">
                {PAD_KEYS.map((k) => (
                    <button key={k} type="button" onClick={() => onKey(k)}
                            data-testid={`numpad-key-${k === "back" ? "back" : k}`}
                            className="h-11 rounded-xl border border-brand-charcoal/10 bg-white shadow-card
                                       font-serif font-bold text-lg text-brand-charcoal">
                        {k === "back" ? "⌫" : k}
                    </button>
                ))}
            </div>
            <button type="button" onClick={onDone} data-testid="numpad-done"
                    className="w-full h-11 mt-2 rounded-xl bg-brand-navy text-brand-bone font-serif font-bold text-sm">
                Done
            </button>
        </div>
    );
}

/* ---------------------------------------------------------- one phase row -- */

function EntryRow({ testId, capLabel, numLabel, name, value, active, canEdit, onOpen, onFocusBox }) {
    const populated = value !== "" && value != null;
    const expanded = active || populated;
    return (
        <div className={`border-t border-brand-charcoal/8 first:border-t-0 ${populated && !active ? "bg-brand-gold/10" : ""}`}
             data-testid={testId}>
            <button type="button" onClick={onOpen} disabled={!canEdit}
                    className={`w-full flex items-center gap-2.5 px-4 text-left disabled:cursor-default ${expanded ? "pt-2.5 pb-1" : "py-2.5"}`}
                    data-testid={`${testId}-head`}>
                <span className="flex-1 min-w-0">
                    {capLabel && (
                        <span className="block text-[9px] uppercase tracking-widest text-brand-admin-header font-bold mb-0.5">
                            {capLabel}
                        </span>
                    )}
                    <span className={`transition-all ${
                        active
                            ? "text-[11px] font-semibold text-brand-charcoal/60"
                            : populated
                                ? "text-[15px] font-bold text-brand-charcoal"
                                : "text-[13.5px] text-brand-charcoal"
                    }`}>
                        {numLabel != null && <span className="text-brand-charcoal/45 text-[10.5px] tabular-nums mr-1.5">{numLabel}</span>}
                        {name}
                    </span>
                </span>
                {!expanded && (
                    <span className="shrink-0 h-5 w-5 rounded-full border-[1.5px] border-dashed border-brand-charcoal/25
                                      text-brand-charcoal/40 text-xs font-bold flex items-center justify-center">+</span>
                )}
            </button>
            <div className="grid transition-[grid-template-rows] duration-200 ease-out"
                 style={{ gridTemplateRows: expanded ? "1fr" : "0fr" }}>
                <div className="overflow-hidden min-h-0" data-testid={`${testId}-clip`}>
                    <div className="px-4 pb-3">
                        <input
                            type="text" inputMode="none" readOnly
                            value={value}
                            placeholder="0.0"
                            onClick={canEdit ? onFocusBox : undefined}
                            data-testid={`${testId}-box`}
                            className={`w-full h-12 rounded-xl text-center font-serif font-bold text-xl text-brand-navy
                                       tabular-nums border-[1.5px] ${active
                                ? "border-brand-navy bg-white shadow-[0_0_0_3px_rgba(17,24,39,0.14)]"
                                : value ? "border-brand-gold bg-white" : "border-brand-charcoal/20 bg-brand-bone"}`}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------- job block -- */

function JobSection({ project, values, activeKey, canEdit, onOpenField, phases, phaseName }) {
    const [showAll, setShowAll] = useState(false);
    // Collapsed, a job shows only what's already logged on it today -- tap
    // the header to see the rest of the catalog. A row that holds a number
    // never hides, expanded or not.
    const populatedNums = Object.keys(values)
        .map(Number)
        .filter((n) => values[n] !== "" && values[n] != null)
        .sort((a, b) => a - b);
    const catalogNums = phases.map((p) => p.phase_number);
    const allNums = [...new Set([...catalogNums, ...populatedNums])].sort((a, b) => a - b);
    const hasMore = allNums.length > populatedNums.length;
    const visibleNums = showAll ? allNums : populatedNums;
    const subtotal = Object.values(values).reduce((s, v) => s + (parseFloat(v) || 0), 0);

    return (
        <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card overflow-hidden"
             data-testid={`job-section-${project.project_id}`}>
            <button type="button" onClick={hasMore ? () => setShowAll((v) => !v) : undefined}
                    disabled={!hasMore}
                    data-testid={`job-header-${project.project_id}`}
                    className="w-full bg-brand-stone px-4 py-2 flex items-baseline gap-2 text-left disabled:cursor-default">
                <span className="font-bold text-[13px] text-brand-charcoal">{project.name}</span>
                <span className="text-[10.5px] text-brand-charcoal/65">{project.job_number}</span>
                <span className="ml-auto text-[12px] font-bold tabular-nums text-brand-charcoal"
                      data-testid={`job-subtotal-${project.project_id}`}>
                    {subtotal.toFixed(2)}h
                </span>
                {hasMore && (
                    <ChevronDown className={`h-3.5 w-3.5 text-brand-charcoal/50 shrink-0 transition-transform ${showAll ? "rotate-180" : ""}`} />
                )}
            </button>
            {visibleNums.map((n) => (
                <EntryRow key={n} testId={`phase-row-${project.project_id}-${n}`}
                          numLabel={n} name={phaseName(n)}
                          value={values[n] ?? ""}
                          active={activeKey === n} canEdit={canEdit}
                          onOpen={() => onOpenField(n)}
                          onFocusBox={() => onOpenField(n, true)} />
            ))}
        </div>
    );
}

/* ------------------------------------------------------------------ sheet -- */

export default function DailyEntrySheet({ timecard, date, entries, projects = [], canEdit, onClose, onChanged }) {
    const { phases, phaseName } = usePhases();

    // Every active project gets a section, always -- real jobs, then general
    // (non-billable) tasks, then the PTO bucket last. Nothing to "add"; the
    // list is Kim's whole active project set, kept current from Admin ->
    // Projects, and a section with nothing typed into it just means nothing
    // was logged there today.
    const jobs = useMemo(() => projects.filter((p) => !p.pto_kind && !p.task_kind && p.active !== false), [projects]);
    const taskProjects = useMemo(() => projects.filter((p) => p.task_kind && p.active !== false), [projects]);
    const ptoProjects = useMemo(() => projects.filter((p) => p.pto_kind && p.active !== false), [projects]);
    const noPhaseProjects = useMemo(() => [...taskProjects, ...ptoProjects], [taskProjects, ptoProjects]);

    // Per job: { entry_id, values: {phaseNum: "4"} }.
    // Per no-phase project: { entry_id, value: "8" }.
    const [jobState, setJobState] = useState({});
    const [singleState, setSingleState] = useState({});
    const [mealOn, setMealOn] = useState(true);
    const [activeField, setActiveField] = useState(null); // { projectId, phaseNum } | { projectId } for single rows | null
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        const byProject = {};
        (entries || []).forEach((e) => { if (e.project_id) byProject[e.project_id] = e; });

        const nextJobState = {};
        jobs.forEach((p) => {
            const e = byProject[p.project_id];
            const split = e ? phaseSplit(e) : {};
            const values = {};
            Object.entries(split).forEach(([n, h]) => { if (h) values[n] = String(h); });
            nextJobState[p.project_id] = { entry_id: e?.entry_id, values };
        });
        setJobState(nextJobState);

        const nextSingleState = {};
        noPhaseProjects.forEach((p) => {
            const e = byProject[p.project_id];
            nextSingleState[p.project_id] = { entry_id: e?.entry_id, value: e ? String(round2(e.hours) || "") : "" };
        });
        setSingleState(nextSingleState);

        let alive = true;
        if (timecard?.timecard_id) {
            api.get(`/timecards/${timecard.timecard_id}/meals`)
                .then((r) => {
                    const d = r.data?.days?.[date];
                    if (alive) setMealOn(d?.minutes == null ? true : d.minutes > 0);
                })
                .catch(() => { /* starts at the default (on) */ });
        }
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries, date, jobs, noPhaseProjects]);

    /* ------------------------------------------------------ field editing -- */

    const openField = (projectId, phaseNum, focusOnly) => {
        const isThisOne = activeField?.projectId === projectId && activeField?.phaseNum === phaseNum;
        if (isThisOne && !focusOnly) {
            setActiveField(null); // second tap on the same (still-editable) field closes it
            return;
        }
        setActiveField({ projectId, phaseNum });
    };

    const fieldValue = (projectId, phaseNum) => {
        if (phaseNum == null) return singleState[projectId]?.value ?? "";
        return jobState[projectId]?.values?.[phaseNum] ?? "";
    };

    const setFieldValue = (projectId, phaseNum, next) => {
        if (phaseNum == null) {
            setSingleState((cur) => ({ ...cur, [projectId]: { ...cur[projectId], value: next } }));
        } else {
            setJobState((cur) => ({
                ...cur,
                [projectId]: { ...cur[projectId], values: { ...cur[projectId].values, [phaseNum]: next } },
            }));
        }
    };

    const padKey = (key) => {
        if (!activeField) return;
        const cur = fieldValue(activeField.projectId, activeField.phaseNum) || "";
        let next;
        if (key === "back") next = cur.slice(0, -1);
        else if (key === ".") next = cur.includes(".") ? cur : cur + ".";
        else next = cur.length < 4 ? cur + key : cur;
        setFieldValue(activeField.projectId, activeField.phaseNum, next);
    };
    const padPreset = (n) => {
        if (!activeField) return;
        setFieldValue(activeField.projectId, activeField.phaseNum, String(n));
    };
    const padClear = () => {
        if (!activeField) return;
        setFieldValue(activeField.projectId, activeField.phaseNum, "");
    };
    const padDone = () => setActiveField(null);

    const activePadLabel = useMemo(() => {
        if (!activeField) return "";
        if (activeField.phaseNum == null) {
            const p = noPhaseProjects.find((x) => x.project_id === activeField.projectId);
            return p?.name || "";
        }
        return `${activeField.phaseNum} · ${phaseName(activeField.phaseNum)}`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeField, noPhaseProjects]);

    /* ------------------------------------------------------------- total -- */

    const workedTotal = useMemo(() => {
        let sum = 0;
        Object.values(jobState).forEach((b) => Object.values(b.values || {}).forEach((v) => { sum += parseFloat(v) || 0; }));
        Object.values(singleState).forEach((b) => { sum += parseFloat(b.value) || 0; });
        return sum;
    }, [jobState, singleState]);
    // The total is exactly what's typed on phases -- the meal toggle is a
    // status flag next to it, never arithmetic added on top of it.
    const dayTotal = workedTotal;

    const touchedJobCount = Object.values(jobState).filter((b) => Object.values(b.values || {}).some((v) => parseFloat(v) > 0)).length
        + Object.values(singleState).filter((b) => parseFloat(b.value) > 0).length;
    const ready = touchedJobCount > 0;

    /* -------------------------------------------------------------- save -- */

    const save = async () => {
        setErr(null);
        setSaving(true);
        try {
            const keptIds = new Set();
            for (const p of jobs) {
                const b = jobState[p.project_id];
                const nonzero = Object.entries(b?.values || {}).filter(([, v]) => parseFloat(v) > 0);
                if (!nonzero.length) continue;
                const phase_hours = Object.fromEntries(nonzero.map(([n, v]) => [n, round2(v)]));
                const phase_numbers = nonzero.map(([n]) => Number(n));
                const hours = round2(Object.values(phase_hours).reduce((s, v) => s + v, 0));
                const body = { timecard_id: timecard.timecard_id, date, project_id: p.project_id, phase_numbers, hours, phase_hours };
                if (b.entry_id) { await api.patch(`/entries/${b.entry_id}`, body); keptIds.add(b.entry_id); }
                else { const r = await api.post("/entries", body); keptIds.add(r.data?.entry_id); }
            }
            for (const p of noPhaseProjects) {
                const b = singleState[p.project_id];
                const hours = round2(b?.value);
                if (!(hours > 0)) continue;
                const body = { timecard_id: timecard.timecard_id, date, project_id: p.project_id, phase_numbers: [], hours, phase_hours: null };
                if (b.entry_id) { await api.patch(`/entries/${b.entry_id}`, body); keptIds.add(b.entry_id); }
                else { const r = await api.post("/entries", body); keptIds.add(r.data?.entry_id); }
            }
            // Anything that had a saved entry but was cleared back to blank
            // means the crew member deleted that entry.
            for (const e of entries || []) {
                if (e.entry_id && !keptIds.has(e.entry_id)) await api.delete(`/entries/${e.entry_id}`);
            }
            // Always recorded as taken -- never asked. The toggle only
            // changes the minutes recorded here; it never touches the day
            // total above, which is exactly what's typed into the phase
            // boxes and nothing else.
            await api.post("/timecards/meal", {
                timecard_id: timecard.timecard_id, date,
                taken: true, note: null,
                minutes: mealOn ? MEAL_MINUTES_ON : 0,
            });
            toast.success("Day saved");
            onChanged?.();
            onClose();
        } catch (e2) {
            setErr(describeRequestError(e2, "Could not save the day."));
        } finally {
            setSaving(false);
        }
    };

    return (
        // !mt-0 fights a real bug, not decoration: CrewApp (and ForemanApp's
        // reuse of it for "My Hours") renders this inside a `space-y-4`
        // wrapper, and this is rarely the first child there. Tailwind's
        // sibling-spacing rule still gives a `fixed` element a 16px
        // margin-top even though it's meant to cover the whole viewport --
        // margin isn't cancelled by fixed positioning, it just shifts the
        // fixed box. The symptom was a sliver of AppHeader peeking through
        // above this screen's own header, worst on wide/desktop viewports
        // where it's easy to notice.
        <div className="fixed inset-0 z-50 flex flex-col bg-brand-bone !mt-0" data-testid="daily-entry-sheet">
            <div className="bg-brand-charcoal text-brand-bone px-5 py-4 flex items-center gap-3 shrink-0">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-brand-gold">Daily entry</div>
                    <h2 className="font-serif font-bold text-xl">{dayjs(date).format("dddd, MMM D")}</h2>
                </div>
                <button onClick={onClose} data-testid="close-entry-sheet"
                        className="ml-auto p-2 hover:bg-white/10 rounded-lg"><X className="h-5 w-5" /></button>
            </div>

            {/* Pinned under the header, never scrolls away, live on every tap. */}
            <div className="shrink-0 bg-brand-bone border-b border-brand-charcoal/12 px-5 py-4 flex items-center gap-4"
                 data-testid="day-total-card">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.22em] text-brand-admin-header font-bold">Day total</div>
                    <div className="text-[13px] text-brand-charcoal/70 font-semibold tabular-nums" data-testid="day-total-breakdown">
                        {workedTotal.toFixed(2)} worked{mealOn ? " · lunch taken" : " · lunch skipped"}
                    </div>
                </div>
                <div className="ml-auto font-serif font-bold text-5xl text-brand-navy tabular-nums" data-testid="day-total">
                    {dayTotal.toFixed(2)}h
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {err && (
                    <div className="text-sm text-brand-danger bg-brand-danger/10 border border-brand-danger/30 rounded-xl px-3 py-2"
                         data-testid="entry-error">{err}</div>
                )}

                <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card px-3 py-2.5"
                         data-testid="meal-block">
                        <div className="flex items-center gap-1.5">
                            <Utensils className="h-3.5 w-3.5 text-brand-charcoal/50 shrink-0" />
                            <span className="font-bold text-[12px] text-brand-charcoal">Lunch</span>
                            <button
                                type="button"
                                onClick={() => setMealOn((v) => !v)}
                                disabled={!canEdit}
                                data-testid="meal-toggle"
                                aria-pressed={mealOn}
                                className={`ml-auto relative w-9 h-5 rounded-full transition-colors shrink-0 ${mealOn ? "bg-brand-success" : "bg-brand-stone"}`}
                            >
                                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${mealOn ? "left-[18px]" : "left-0.5"}`} />
                            </button>
                        </div>
                        <div className="text-[10px] text-brand-charcoal/55 mt-1" data-testid="meal-status">
                            {mealOn ? "Taken today" : "Skipped today"}
                        </div>
                    </div>

                    {ptoProjects[0] && (
                        <div className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card overflow-hidden"
                             data-testid="pto-block">
                            <EntryRow testId={`single-row-${ptoProjects[0].project_id}`} name={ptoProjects[0].name}
                                      value={singleState[ptoProjects[0].project_id]?.value ?? ""}
                                      active={activeField?.projectId === ptoProjects[0].project_id && activeField?.phaseNum === undefined}
                                      canEdit={canEdit}
                                      onOpen={() => openField(ptoProjects[0].project_id, undefined)}
                                      onFocusBox={() => openField(ptoProjects[0].project_id, undefined, true)} />
                        </div>
                    )}
                </div>

                {jobs.map((p) => (
                    <JobSection key={p.project_id} project={p}
                                values={jobState[p.project_id]?.values || {}}
                                activeKey={activeField?.projectId === p.project_id ? activeField.phaseNum : undefined}
                                canEdit={canEdit}
                                onOpenField={(n, focusOnly) => openField(p.project_id, n, focusOnly)}
                                phases={phases} phaseName={phaseName} />
                ))}

                {taskProjects.map((p) => (
                    <div key={p.project_id} className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card overflow-hidden">
                        <EntryRow testId={`single-row-${p.project_id}`} capLabel="Company time" name={p.name}
                                  value={singleState[p.project_id]?.value ?? ""}
                                  active={activeField?.projectId === p.project_id && activeField?.phaseNum === undefined}
                                  canEdit={canEdit}
                                  onOpen={() => openField(p.project_id, undefined)}
                                  onFocusBox={() => openField(p.project_id, undefined, true)} />
                    </div>
                ))}
                {ptoProjects.slice(1).map((p) => (
                    <div key={p.project_id} className="bg-white rounded-2xl border border-brand-charcoal/15 shadow-card overflow-hidden">
                        <EntryRow testId={`single-row-${p.project_id}`} capLabel="Personal" name={p.name}
                                  value={singleState[p.project_id]?.value ?? ""}
                                  active={activeField?.projectId === p.project_id && activeField?.phaseNum === undefined}
                                  canEdit={canEdit}
                                  onOpen={() => openField(p.project_id, undefined)}
                                  onFocusBox={() => openField(p.project_id, undefined, true)} />
                    </div>
                ))}
            </div>

            <div className="shrink-0 border-t border-brand-charcoal/10 bg-brand-bone">
                {activeField && canEdit ? (
                    <NumPad label={activePadLabel} value={fieldValue(activeField.projectId, activeField.phaseNum)}
                            onKey={padKey} onPreset={padPreset} onDone={padDone} onClear={padClear} />
                ) : canEdit ? (
                    <div className="p-4">
                        <button
                            onClick={save}
                            disabled={!ready || saving}
                            data-testid="done-for-day-button"
                            className="w-full h-14 rounded-2xl bg-brand-charcoal text-brand-bone font-serif font-bold text-base
                                       inline-flex items-center justify-center gap-2 disabled:opacity-40"
                        >
                            <Check className="h-5 w-5" />
                            {saving ? "Saving…" : "Done for the day"}
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
